import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, IconButton, List, ListItem, ListItemText, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { botJson } from './api.js';
import { copyText, shareUrlOf } from './ShareDialog';

// Semua link share akun ini (bagian dari Setelan), supaya link mana pun bisa
// dicabut tanpa harus mencari file-nya dulu. Link yang file-nya sudah dihapus
// juga muncul di sini.
export default function ShareLinksSettings({ open, fileManager }) {
    const [shares, setShares] = useState(null);
    const [bases, setBases] = useState([]);
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState(null);
    const [copiedId, setCopiedId] = useState(null);

    const refresh = async () => {
        try {
            const data = await botJson(`/share/${fileManager.userId}`);
            setBases(data.bases || []);
            setShares(data.shares || []);
        } catch (e) {
            setError(e.message);
            setShares((current) => current || []);
        }
    };

    useEffect(() => {
        if (!open || !fileManager) return;
        setError(null);
        setShares(null);
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, fileManager]);

    const revoke = async (ids) => {
        setError(null);
        setBusyId(ids.length > 1 ? 'all' : ids[0]);
        try {
            for (const id of ids) {
                await botJson(`/share/${fileManager.userId}/${encodeURIComponent(id)}`, { method: 'DELETE' });
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setBusyId(null);
            await refresh();
        }
    };

    const onRevoke = (share) => {
        if (!window.confirm(`Hapus link untuk "${share.name}"? Siapa pun yang punya link ini tidak bisa membukanya lagi.`)) return;
        revoke([share.id]);
    };

    const onRevokeAll = () => {
        if (!window.confirm(`Hapus SEMUA ${shares.length} link? Semua link yang sudah dibagikan langsung tidak bisa dibuka lagi.`)) return;
        revoke(shares.map((s) => s.id));
    };

    const onCopy = async (share) => {
        await copyText(shareUrlOf(share, bases));
        setCopiedId(share.id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    return (
        <>
            <Typography variant="subtitle2">Link share</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Semua link /s/ yang masih aktif. Link yang dihapus langsung tidak bisa dibuka lagi; file-nya sendiri tidak ikut terhapus.
            </Typography>
            {shares === null && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>}
            {shares && shares.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Tidak ada link yang aktif.</Typography>
            )}
            {shares && shares.length > 0 && (
                <>
                    <List dense sx={{ maxHeight: 360, overflowY: 'auto' }}>
                        {shares.map((share) => (
                            <ListItem key={share.id} disableGutters secondaryAction={
                                <Box>
                                    <Tooltip title="Salin link"><IconButton edge="end" size="small" onClick={() => onCopy(share)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                                    <Tooltip title="Hapus link">
                                        <span>
                                            <IconButton edge="end" size="small" disabled={Boolean(busyId)} onClick={() => onRevoke(share)}>
                                                {busyId === share.id ? <CircularProgress size={16} /> : <DeleteOutlineIcon fontSize="small" color="error" />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            }>
                                <ListItemText
                                    primary={share.name}
                                    secondary={
                                        <>
                                            <Box component="span" sx={{ display: 'block', wordBreak: 'break-all' }}>
                                                {copiedId === share.id ? 'Tersalin!' : shareUrlOf(share, bases)}
                                            </Box>
                                            {`${share.path} · ${share.expiresAt ? `sampai ${new Date(share.expiresAt).toLocaleString('id-ID')}` : 'tanpa batas'}${share.hasPassword ? ' · pakai password' : ''} · dibuka ${share.views}x`}
                                        </>
                                    }
                                    primaryTypographyProps={{ sx: { wordBreak: 'break-all', pr: 8 } }}
                                    secondaryTypographyProps={{ component: 'div', sx: { wordBreak: 'break-all', pr: 8 } }}
                                />
                            </ListItem>
                        ))}
                    </List>
                    <Button variant="outlined" color="error" size="small" disabled={Boolean(busyId)} onClick={onRevokeAll}
                        startIcon={busyId === 'all' ? <CircularProgress size={16} /> : <DeleteOutlineIcon />}>
                        Hapus semua link ({shares.length})
                    </Button>
                </>
            )}
            {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        </>
    );
}
